<script setup>
import { ref, reactive, onMounted, defineEmits } from 'vue'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/errorhandler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanPiutangUsahaService from '@/services/LaporanPiutangUsahaService'
import SalesmanService from '@/services/SalesmanService'
import useVuelidate from '@vuelidate/core'
import AreaService from '@/services/AreaService'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanPiutangUsahaService()
const salesmanService = new SalesmanService()
const areaService = new AreaService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
let isLoadingExportExcel = ref(false)
let form = reactive({
  date_to: null,
  salesman: null,
  area: null,
})
let submitted = ref(false)
let list_salesman = ref([])
let acArea = ref([])

const rules = {
  /*
  salesman: {
    required: helpers.withMessage('Salesman harus diisi.', required),
  },
  */
  date_to: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {
  const res = await salesmanService.get('?sort=nama')
  list_salesman.value = res.data.data
})

function searchArea(e) {
  areaService
    .getBy(`?filter[nama]=${e.query}`)
    .then((res) => {
      acArea.value = res.data.data
    })
    .catch((err) => {
      errorHandler(err, 'Data Pelanggan', toast)
    })
}

function search(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  onExport()
}

function onExport() {
  submitted.value = true
  isLoadingExport.value = true
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')

  let salesmanId = 0
  let areaId = 0
  if (form.salesman) {
    salesmanId = form.salesman
  }
  if (form.area) {
    areaId = form.area.id
  }

  laporanService
    .exportLaporanPerAreaSalesman(endDate, salesmanId, areaId)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-piutang-usaha-per-area-salesman.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Piutang Usaha Per Area Salesman', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onExportExcel(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  submitted.value = true
  isLoadingExportExcel.value = true
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')

  let salesmanId = 0
  let areaId = 0
  if (form.salesman) {
    salesmanId = form.salesman
  }
  if (form.area) {
    areaId = form.area.id
  }

  laporanService
    .exportExcelLaporanPerAreaSalesman(endDate, salesmanId, areaId)
    .then((res) => {
      isLoadingExportExcel.value = false
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        'laporan-piutang-usaha-per-area-salesman.xls'
      )
      document.body.appendChild(link)
      link.click()
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Piutang Usaha Per Area Salesman', toast)
    })
    .finally(() => (isLoadingExportExcel.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  submitted.value = false
  form.date_to = null
  form.salesman = null
  form.area = null
}
</script>

<template>
  <fx-report-container
    title="Laporan Piutang Per Area Salesman"
    :loading="isLoadingExport"
    @toggle="onToggle"
    @export="onExport"
  >
    <template #content>
      <form @submit.prevent="search(!v$.$invalid)">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-3">
            <Dropdown
              v-model="form.salesman"
              :options="list_salesman"
              optionValue="id"
              optionLabel="nama"
              placeholder="Pilih Salesman"
              class="w-full"
            />
            <!--
            <small
              v-if="
                (v$.salesman.$invalid && submitted) ||
                v$.salesman.$pending.$response
              "
              class="p-error"
              >{{ v$.salesman.required.$message }}</small
            >
            -->
          </div>
          <div class="field col-12 md:col-2">
            <AutoComplete
              v-model="form.area"
              :min-length="2"
              :suggestions="acArea"
              field="nama"
              class="w-full"
              input-class="w-full"
              placeholder="Area"
              autofocus
              @complete="searchArea($event)"
            >
            </AutoComplete>
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-4">
            <Button
              :loading="isLoadingExport"
              type="submit"
              icon="pi pi-file-pdf"
              label="Cetak"
              class="mr-2 mb-0 md:mb-2 xl:mb-0"
            />
            <Button
              :loading="isLoadingExportExcel"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success mr-2 mb-0 md:mb-2 xl:mb-0"
              @click="onExportExcel(!v$.$invalid)"
            />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined btn-2xl-mt-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
